// ============================================================
// Olivium Secure Access (V2) — data, target registry, API wrappers, hook.
//
// PHASE_2L part 4: a V2 hub over the EXISTING, already-applied Secure Access
// spine (sq_access_codes). It reuses api.commandCenter.access/accessCodes.* —
// hash-at-rest codes, one-time plaintext reveal, target binding, audited
// create/revoke/expire. NOTHING is faked: codes are real and admin-gated
// (minLevel 80); the one-time plaintext is shown exactly once; a code bound to
// an unrouted placeholder target honestly reports "not bound to a destination".
//
// The 5 universal PHASE_2L target types (project_portal / repo_context /
// *_placeholder) require the target_type CHECK widening (20260710000001) to be
// applied before a code can be issued against them; until then they are
// pending-migration and issuing against them fails honestly (invalid_target_type
// / DB check) rather than fabricating a grant. GitHub repo permission is never
// granted from here (GITHUB_PERMISSION_DEFERRED).
// ============================================================

import { COMMAND_CENTER_ROOT } from '../../commandCenterRoutes'
import { useCallback, useEffect, useState } from 'react'
import {
  api, isAuthError, isForbiddenError,
  type CommandCenterAccessCode, type AccessCodeTargetType,
} from '../../../../api/client'

const CC = COMMAND_CENTER_ROOT
export const SECURE_ACCESS_ROOT = `${CC}/olivium/secure-access`
export const SECURE_ACCESS_CREATE_ROOT = `${CC}/olivium/secure-access/new`
export function secureAccessDetailPath(id: string): string { return `${SECURE_ACCESS_ROOT}/${id}` }

// The universal target registry — the 7 existing + 5 PHASE_2L types. phase2l
// entries need the widening migration applied to issue against them.
export interface SecureTargetType { value: AccessCodeTargetType; label: string; phase2l?: boolean; note?: string }
export const SECURE_TARGET_TYPES: SecureTargetType[] = [
  { value: 'project_portal', label: 'Project portal', phase2l: true, note: 'Binds to a V2 project portal (target_id = portal id).' },
  { value: 'repo_context', label: 'Repo context', phase2l: true, note: 'Opens a portal repo surface. GitHub permission is NOT granted (GITHUB_PERMISSION_DEFERRED).' },
  { value: 'command_packet_placeholder', label: 'Command packet (placeholder)', phase2l: true, note: 'Not routed yet — redeem reports no destination honestly.' },
  { value: 'document_or_file_placeholder', label: 'Document / file (placeholder)', phase2l: true, note: 'Not routed yet.' },
  { value: 'client_workspace_placeholder', label: 'Client workspace (placeholder)', phase2l: true, note: 'No live client portal yet (CLIENT_ACCESS_DEFERRED).' },
  { value: 'client_portal', label: 'Client portal (secure share)' },
  { value: 'project_workspace', label: 'Project workspace' },
  { value: 'document_workspace', label: 'Document workspace' },
  { value: 'document_artifact', label: 'Document artifact' },
  { value: 'spreadsheet_artifact', label: 'Spreadsheet artifact' },
  { value: 'intake_invite', label: 'Intake invite' },
  { value: 'secure_portal', label: 'Secure portal' },
]

// The persisted status vocabulary of sq_access_codes (the REAL 4). We surface
// these honestly rather than fake the broader draft/issued/redeemed/denied
// lifecycle — the spine records exactly these.
export const ACCESS_STATUS_META: Record<string, { label: string; accent: string }> = {
  active: { label: 'ACTIVE', accent: '#059669' },
  claimed: { label: 'CLAIMED', accent: '#0891b2' },
  revoked: { label: 'REVOKED', accent: '#dc2626' },
  expired: { label: 'EXPIRED', accent: '#6a7480' },
}

export type ExpiresIn = '1h' | '24h' | '7d' | '30d' | 'never'

export interface SecureAccessListState {
  codes: CommandCenterAccessCode[]
  summary: { active: number; claimed: number; revoked: number; expired: number; total: number } | null
  loading: boolean
  error: string | null
  authRequired: boolean
  forbidden: boolean
}

export function useSecureAccess() {
  const [state, setState] = useState<SecureAccessListState>({ codes: [], summary: null, loading: true, error: null, authRequired: false, forbidden: false })

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const res = await api.commandCenter.access()
      setState({ codes: res.codes || [], summary: res.summary ?? null, loading: false, error: null, authRequired: false, forbidden: false })
    } catch (err) {
      if (isAuthError(err)) { setState({ codes: [], summary: null, loading: false, error: null, authRequired: true, forbidden: false }); return }
      if (isForbiddenError(err)) { setState({ codes: [], summary: null, loading: false, error: null, authRequired: false, forbidden: true }); return }
      const msg = (err as { message?: string })?.message || 'Could not reach the Secure Access backend.'
      setState({ codes: [], summary: null, loading: false, error: msg, authRequired: false, forbidden: false })
    }
  }, [])

  useEffect(() => { load() }, [load])
  return { ...state, reload: load }
}

export interface IssuePayload {
  label: string
  audience?: 'operator' | 'team' | 'client'
  role?: 'operator' | 'client_owner' | 'client_collaborator' | 'viewer'
  max_uses?: number
  expires_in?: ExpiresIn
  target_type?: AccessCodeTargetType
  target_id?: string
  target_label?: string
  project_id?: string
}

export async function issueSecureAccess(payload: IssuePayload) {
  // Returns the one-time plaintext access_code exactly once.
  return api.commandCenter.accessCodes.create(payload)
}
export async function revokeSecureAccess(code_id: string, reason?: string) {
  return api.commandCenter.accessCodes.revoke({ code_id, reason })
}
export async function expireSecureAccess(code_id: string) {
  return api.commandCenter.accessCodes.expire({ code_id })
}

export function humanizeSecureAccessError(err: unknown): string {
  const anyErr = err as { message?: string; code?: string }
  const code = anyErr?.code || ''
  const msg = anyErr?.message || ''
  if (code === 'invalid_target_type' || /invalid_target_type/i.test(msg)) {
    return 'That target type is not accepted yet — apply migration 20260710000001 (Secure Access target widening) first.'
  }
  if (/relation .* does not exist/i.test(msg) || /check constraint/i.test(msg)) {
    return 'The Secure Access target widening is not migrated yet (apply migration 20260710000001).'
  }
  return msg || 'Could not complete the Secure Access action.'
}
